import { Route, Routes } from 'react-router-dom'
import CbamSimulationDetailsPage from './pages/cbam-simulation-details'
import { Button, H2, PageSection, PromoBanner } from '@/common'
import { PlusIcon } from '@/common/components/icons'
import CbamSimulationsTable from './components/CbamSimulationsTable'
import { Container } from '@/shared/components'
import useI18n from '@/shared/hooks/usei18n'
import useDialog from '@/common/hooks/useDialog'
import CreateCbamSimulationDialog from './dialogs/CreateCbamSimulationDialog'
import { useGetAllCbamSimulations } from '@/features/cbam-simulations-v2/hooks/cbamSimulations'
import { useSession } from '@/shared/hooks/useSession'
import DeleteCbamSimulationDialog from './dialogs/DeleteCbamSimulationDialog'
import { useCallback, useState } from 'react'
import { useTrackCbamSimulationListViewedOnce } from '@/features/cbam-simulations-v2/hooks/useCbamSimulationClarityTracking'

export default function CbamSimulationsV2() {
  return (
    <Routes>
      <Route path="/" element={<CbamSimulationsPage />} />
      <Route
        path="/:cbamSimulationId"
        element={<CbamSimulationDetailsPage simulationYear={2026} />}
      />
    </Routes>
  )
}

function CbamSimulationsPage() {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const cbamSimulationsQuery = useGetAllCbamSimulations(organizationId)

  const cbamSimulations = cbamSimulationsQuery.data ?? []

  useTrackCbamSimulationListViewedOnce()

  const [simulationIdPendingDelete, setSimulationIdPendingDelete] = useState<string | null>(null)

  const {
    open: openCreateSimulation,
    openDialog: openCreateSimulationDialog,
    closeDialog: closeCreateSimulationDialog
  } = useDialog()

  const {
    open: openDeleteSimulation,
    openDialog: openDeleteSimulationDialog,
    closeDialog: closeDeleteSimulationDialog
  } = useDialog()

  const onDeleteSimulation = useCallback(
    (simulationId: string) => {
      setSimulationIdPendingDelete(simulationId)
      openDeleteSimulationDialog()
    },
    [openDeleteSimulationDialog]
  )

  const hasAnySimulations = cbamSimulations.length > 0

  return (
    <>
      <Container>
        <H2>{t('cbamSimulations:cbamSimulationsPage.title')}</H2>
        <PageSection
          title={t('cbamSimulations:cbamSimulationsPage.cbamSimulationsList.title')}
          actions={
            hasAnySimulations && (
              <Button
                icon={<PlusIcon className="size-6" />}
                className="w-48"
                onClick={openCreateSimulationDialog}
              >
                {t('cbamSimulations:cbamSimulationsPage.cbamSimulationsList.actions.create')}
              </Button>
            )
          }
        >
          {hasAnySimulations ? (
            <CbamSimulationsTable
              simulationIds={cbamSimulations.map(simulation => simulation.id)}
              onDeleteSimulation={onDeleteSimulation}
            />
          ) : (
            <PromoBanner
              title={t(
                'cbamSimulations:cbamSimulationsPage.cbamSimulationsList.noSimulationsFound.title'
              )}
              description={t(
                'cbamSimulations:cbamSimulationsPage.cbamSimulationsList.noSimulationsFound.description'
              )}
              action={
                <Button
                  icon={<PlusIcon className="size-6" />}
                  className="w-48"
                  onClick={openCreateSimulationDialog}
                >
                  {t('cbamSimulations:cbamSimulationsPage.cbamSimulationsList.actions.create')}
                </Button>
              }
            />
          )}
        </PageSection>
      </Container>
      <CreateCbamSimulationDialog
        isOpen={openCreateSimulation}
        close={closeCreateSimulationDialog}
      />
      {simulationIdPendingDelete && (
        <DeleteCbamSimulationDialog
          isOpen={openDeleteSimulation}
          close={closeDeleteSimulationDialog}
          simulationName={
            cbamSimulations.find(simulation => simulation.id === simulationIdPendingDelete)?.name ??
            ''
          }
          simulationId={simulationIdPendingDelete}
          onAfterLeave={() => setSimulationIdPendingDelete(null)}
        />
      )}
    </>
  )
}
